import { useState, useEffect, useCallback } from 'react';

const LOGO_IMAGE = "https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20logo%20design%20for%20a%20water%20management%20company%20featuring%20a%20stylized%20water%20droplet%20combined%20with%20interconnected%20network%20nodes%20in%20emerald%20green%20tones%20clean%20geometric%20shapes%20white%20background%20professional%20corporate%20identity%20design%20simple%20flat%20vector%20style%20crisp%20edges%20transparent%20feel&width=400&height=200&seq=3&orientation=landscape";

const HERO_IMAGE = "https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20illustration%20of%20a%20rural%20Chilean%20landscape%20at%20golden%20hour%20with%20rolling%20green%20hills%20a%20modern%20elevated%20water%20tank%20on%20a%20hilltop%20a%20winding%20river%20through%20the%20valley%20warm%20orange%20and%20golden%20sky%20abstract%20geometric%20water%20droplets%20floating%20in%20the%20air%20clean%20minimalist%20vector%20art%20style%20with%20warm%20earth%20tones%20soft%20gradients%20professional%20and%20modern%20aesthetic%20wide%20panoramic%20composition&width=1800&height=1200&seq=1&orientation=landscape";

const CTA_IMAGE = "https://readdy.ai/api/search-image?query=Abstract%20artistic%20representation%20of%20flowing%20water%20in%20warm%20sunset%20colors%20geometric%20wave%20patterns%20in%20orange%20amber%20and%20coral%20tones%20smooth%20flowing%20lines%20representing%20water%20management%20technology%20modern%20minimalist%20design%20with%20glowing%20particles%20professional%20clean%20aesthetic%20warm%20golden%20and%20terracotta%20color%20palette%20digital%20art%20style&width=1800&height=800&seq=2&orientation=landscape";

const SII_LOGO = "https://static.readdy.ai/image/a0a6306f68c3ce17ffa83b3fadfd58c2/a0c48a6a2c803c5130ab5312b829e473.png";

const FORM_URL = "https://readdy.ai/api/form/d8f2ue0jl3r96eih9i60";

const benefits = [
  {
    icon: "ri-focus-3-line",
    title: "Más Control",
    description: "Centralice toda la información operacional y financiera de su APR en un solo lugar, con acceso en tiempo real desde cualquier dispositivo."
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Más Recaudación",
    description: "Facilite el pago de cuentas mediante canales digitales modernos, aumentando la recaudación y reduciendo la morosidad de sus socios."
  },
  {
    icon: "ri-shield-check-line",
    title: "Menos Errores",
    description: "Elimine procesos manuales y duplicidad de información. Automatice tareas repetitivas y reduzca drásticamente los errores operativos."
  },
  {
    icon: "ri-eye-line",
    title: "Más Transparencia",
    description: "Genere reportes claros y detallados para la administración, directiva y organismos fiscalizadores con total trazabilidad."
  }
];

const modules = [
  {
    icon: "ri-group-line",
    title: "Gestión Comercial",
    description: "Administre socios, clientes, contratos y consumos de forma centralizada y eficiente."
  },
  {
    icon: "ri-file-text-line",
    title: "Facturación Electrónica",
    description: "Integración directa con el SII sin procesos externos. Emita documentos tributarios electrónicos sin complicaciones.",
    siiLogo: true
  },
  {
    icon: "ri-smartphone-line",
    title: "Lectura Móvil",
    description: "Capture lecturas de medidores desde dispositivos Android en terreno, con y sin conexión a internet."
  },
  {
    icon: "ri-computer-line",
    title: "Portal de Usuarios",
    description: "Sus socios pueden consultar su estado de cuenta, descargar documentos y pagar en línea 24/7."
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Reportería",
    description: "Información administrativa, operacional y financiera con dashboards interactivos y exportables.",
    badge: "Reporte Trimestral"
  },
  {
    icon: "ri-archive-line",
    title: "Inventario",
    description: "Gestión completa del inventario de medidores, materiales y activos de su APR. Control de stock en tiempo real con trazabilidad total."
  }
];

const differentiators = [
  { icon: "ri-user-star-line", text: "Usuarios ilimitados" },
  { icon: "ri-file-copy-line", text: "Documentos electrónicos ilimitados" },
  { icon: "ri-mail-line", text: "Correos corporativos ilimitados" },
  { icon: "ri-window-line", text: "Página web personalizada incluida" },
  { icon: "ri-settings-3-line", text: "Plataforma adaptable a cada APR y SSR" },
  { icon: "ri-puzzle-line", text: "Todas las herramientas integradas en una sola solución" }
];

const testimonials = [
  {
    initials: "MC",
    name: "María Cristina Leiva",
    role: "Presidenta",
    apr: "APR El Manzano, Región del Maule",
    quote: "Desde que implementamos APR-Online, la recaudación mensual subió un 40%. Antes perdíamos horas cuadrando planillas Excel. Ahora todo está centralizado y nuestros socios pagan en línea sin problemas.",
    result: "+40% en recaudación"
  },
  {
    initials: "JP",
    name: "José Pablo Figueroa",
    role: "Administrador",
    apr: "APR Los Alerces, Región de Los Lagos",
    quote: "La lectura móvil nos cambió la vida. Nuestros lectores cubren zonas rurales sin señal y la app sincroniza automáticamente al volver. Redujimos errores de digitación en un 95%.",
    result: "−95% en errores de lectura"
  },
  {
    initials: "CR",
    name: "Carmen Rojas",
    role: "Secretaria",
    apr: "APR Agua Clara, Región de O'Higgins",
    quote: "La facturación electrónica integrada con el SII nos ahorró contratar un contador externo. En 15 minutos generamos todas las boletas del mes. Es increíblemente fácil de usar.",
    result: "Facturación en minutos, no días"
  },
  {
    initials: "RV",
    name: "Rodrigo Valenzuela",
    role: "Tesorero",
    apr: "APR Valle Hermoso, Región de Ñuble",
    quote: "Los reportes financieros que antes me tomaban una semana armarlos, ahora los tengo en un click. La directiva está feliz con la transparencia y nosotros dormimos más tranquilos.",
    result: "Reportes instantáneos"
  },
  {
    initials: "PS",
    name: "Patricia Soto",
    role: "Vicepresidenta",
    apr: "SSR Las Vertientes, Región de La Araucanía",
    quote: "Lo que más valoro es que no hay límites: usuarios ilimitados, documentos ilimitados, correos ilimitados. Otras plataformas te cobran por cada cosa extra. APR-Online es todo incluido.",
    result: "Sin límites, sin costos ocultos"
  },
  {
    initials: "GA",
    name: "Gonzalo Aravena",
    role: "Presidente",
    apr: "APR Río Claro, Región del Biobío",
    quote: "Migrar fue rapidísimo. En menos de una semana teníamos todo funcionando. El soporte nos acompañó paso a paso. Hoy nuestros 800 socios tienen portal web propio y pagan desde el celular.",
    result: "800 socios conectados en 1 semana"
  }
];

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded,
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="font-inter bg-white text-neutral-900">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
          <a href="#" className="flex items-center gap-2.5 group">
            <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl overflow-hidden flex items-center justify-center transition-all duration-300 ${scrolled ? 'bg-emerald-50' : 'bg-white/10 backdrop-blur-sm'}`}>
              <img
                src={LOGO_IMAGE}
                alt="APR-Online Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <span className={`font-outfit font-bold text-lg md:text-xl tracking-tight whitespace-nowrap transition-colors duration-300 ${scrolled ? 'text-neutral-900 group-hover:text-emerald-600' : 'text-white group-hover:text-emerald-300'}`}>
              APR<span className="text-emerald-500 group-hover:text-emerald-400 transition-colors">-</span>Online
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {['Inicio', 'Módulos', 'Beneficios', 'Testimonios', 'Planes'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item === 'Inicio' ? 'hero' : item === 'Módulos' ? 'modulos' : item === 'Beneficios' ? 'beneficios' : item === 'Testimonios' ? 'testimonios' : 'planes')}
                className={`text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${scrolled ? 'text-neutral-700 hover:text-emerald-600' : 'text-white/90 hover:text-white'}`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://wa.me/56953907106"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 px-4 py-2.5 rounded-full ${scrolled ? 'text-neutral-700 hover:text-green-600 hover:bg-green-50' : 'text-white/90 hover:text-white hover:bg-white/10'}`}
            >
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <i className="ri-whatsapp-line text-sm text-green-400"></i>
              </div>
              +56 9 5390 7106
            </a>
            <button
              onClick={() => scrollToSection('contacto')}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 ${scrolled ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-white text-emerald-600 hover:bg-white/90'}`}
            >
              Solicitar Demo
            </button>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg ${scrolled ? 'text-neutral-900' : 'text-white'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-neutral-100 px-4 py-4 flex flex-col gap-3">
            {['Inicio', 'Módulos', 'Beneficios', 'Testimonios', 'Planes'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item === 'Inicio' ? 'hero' : item === 'Módulos' ? 'modulos' : item === 'Beneficios' ? 'beneficios' : item === 'Testimonios' ? 'testimonios' : 'planes')}
                className="text-left text-sm font-medium text-neutral-700 py-2 cursor-pointer hover:text-emerald-600 transition-colors"
              >
                {item}
              </button>
            ))}
            <a
              href="https://wa.me/56953907106"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-neutral-700 py-2 cursor-pointer hover:text-green-600 transition-colors"
            >
              <i className="ri-whatsapp-line text-green-500"></i> +56 9 5390 7106
            </a>
            <button
              onClick={() => scrollToSection('contacto')}
              className="mt-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap text-center"
            >
              Solicitar Demo
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative w-full min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Paisaje rural chileno con infraestructura de agua" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 pt-20 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <span className="inline-block px-4 py-1.5 border border-white/40 rounded-full text-white/90 text-xs md:text-sm font-medium tracking-wide mb-6">
                Facturación • Lecturas • Recaudación • Reportes • Portal de Usuarios
              </span>
              <h1 className="font-outfit text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
                La plataforma que moderniza la gestión de los
                <span className="text-emerald-400"> Servicios Sanitarios Rurales</span>
              </h1>
              <p className="text-white/80 text-base md:text-lg max-w-xl mb-8 leading-relaxed">
                Centralice toda la operación de su APR en un solo lugar. Facturación electrónica, lectura móvil en terreno, recaudación digital y reportes inteligentes, todo integrado.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="px-8 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Solicitar Demostración
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button
                  onClick={() => scrollToSection('modulos')}
                  className="px-8 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 border border-white/30"
                >
                  Explorar Módulos
                  <i className="ri-arrow-down-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 md:py-28 px-4 md:px-6 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <span className="text-xs md:text-sm font-semibold text-emerald-600 uppercase tracking-widest">Beneficios</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-neutral-900 mt-3 mb-4">
              ¿Qué obtiene su APR?
            </h2>
            <p className="text-neutral-500 text-base md:text-lg max-w-2xl mx-auto">
              Una solución integral diseñada específicamente para las necesidades de los Servicios Sanitarios Rurales en Chile.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 md:p-8 group hover:bg-emerald-500 transition-all duration-500 cursor-default">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-emerald-50 group-hover:bg-white/20 flex items-center justify-center mb-5 transition-colors duration-500">
                  <i className={`${benefit.icon} text-xl md:text-2xl text-emerald-500 group-hover:text-white transition-colors duration-500`}></i>
                </div>
                <h3 className="font-outfit text-lg md:text-xl font-bold text-neutral-900 group-hover:text-white mb-3 transition-colors duration-500">
                  {benefit.title}
                </h3>
                <p className="text-neutral-500 text-sm md:text-base leading-relaxed group-hover:text-white/90 transition-colors duration-500">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 md:mt-14">
            <p className="text-neutral-500 text-sm mb-4">¿Listo para transformar la gestión de su APR?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => scrollToSection('contacto')}
                className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 inline-flex items-center gap-2"
              >
                Solicitar Demo Gratis
                <i className="ri-arrow-right-line"></i>
              </button>
              <a
                href="https://wa.me/56953907106"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 inline-flex items-center gap-2"
              >
                <i className="ri-whatsapp-line"></i>
                Hablar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modulos" className="py-20 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <span className="text-xs md:text-sm font-semibold text-emerald-600 uppercase tracking-widest">Módulos</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-neutral-900 mt-3 mb-4">
              Todo lo que necesita, en un solo lugar
            </h2>
            <p className="text-neutral-500 text-base md:text-lg max-w-2xl mx-auto">
              Seis módulos integrados que cubren cada aspecto de la gestión de su servicio sanitario rural.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {modules.map((mod, idx) => (
              <div key={idx} className="relative bg-[#fafaf9] rounded-2xl p-6 md:p-7 border border-neutral-100 hover:border-emerald-200 transition-all duration-300 group cursor-default">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${mod.icon} text-lg md:text-xl text-white`}></i>
                </div>
                <h3 className="font-outfit text-base md:text-lg font-bold text-neutral-900 mb-2">
                  {mod.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {mod.description}
                </p>
                {'siiLogo' in mod && (
                  <div className="mt-4 pt-3 border-t border-neutral-200/60">
                    <img
                      src={SII_LOGO}
                      alt="SII - Servicio de Impuestos Internos"
                      className="h-5 md:h-6 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                )}
                {('badge' in mod && mod.badge) && (
                  <div className="mt-4">
                    <span className="inline-block px-4 py-2 bg-emerald-500 text-white font-outfit font-bold text-base md:text-lg rounded-lg whitespace-nowrap">
                      {mod.badge}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12 md:mt-14">
            <p className="text-neutral-500 text-sm mb-4">Todos los módulos incluidos en cada plan, sin costos adicionales</p>
            <button
              onClick={() => scrollToSection('contacto')}
              className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 inline-flex items-center gap-2"
            >
              Quiero una Demostración
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <span className="text-xs md:text-sm font-semibold text-emerald-400 uppercase tracking-widest">Diferenciadores</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mt-3 mb-4">
              ¿Por qué APR-Online es diferente?
            </h2>
            <p className="text-neutral-400 text-base md:text-lg max-w-2xl mx-auto">
              No es solo un software. Es una plataforma completa que incluye todo lo que su APR necesita, sin límites ni costos ocultos.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {differentiators.map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-xl px-5 py-4 md:px-6 md:py-5 border border-white/10 hover:border-emerald-500/30 transition-all duration-300 group cursor-default">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors duration-300">
                  <i className={`${item.icon} text-lg text-emerald-400`}></i>
                </div>
                <span className="text-white font-medium text-sm md:text-base">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" className="py-20 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <span className="text-xs md:text-sm font-semibold text-emerald-600 uppercase tracking-widest">Testimonios</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-neutral-900 mt-3 mb-4">
              APR que ya confían en nosotros
            </h2>
            <p className="text-neutral-500 text-base md:text-lg max-w-2xl mx-auto">
              Conozca las historias reales de Servicios Sanitarios Rurales que transformaron su gestión con APR-Online.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-[#fafaf9] rounded-2xl p-6 md:p-7 border border-neutral-100 hover:border-emerald-200 transition-all duration-300 flex flex-col group cursor-default">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                  ))}
                </div>

                <blockquote className="flex-1">
                  <p className="text-neutral-600 text-sm md:text-base leading-relaxed mb-5 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                </blockquote>

                <div className="flex items-center gap-3 pt-4 border-t border-neutral-200/60">
                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-outfit font-bold text-xs md:text-sm">{t.initials}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-outfit font-semibold text-neutral-900 text-sm truncate">{t.name}</p>
                    <p className="text-neutral-400 text-xs truncate">{t.role} · {t.apr}</p>
                  </div>
                </div>

                <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full">
                  <i className="ri-trophy-line text-xs text-emerald-500"></i>
                  <span className="text-xs font-semibold text-emerald-600 whitespace-nowrap">{t.result}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-12">
            <p className="text-neutral-400 text-sm mb-4">Más de 50 APR en todo Chile ya usan APR-Online</p>
            <button
              onClick={() => scrollToSection('contacto')}
              className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 inline-flex items-center gap-2"
            >
              Únase a ellos
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planes" className="py-20 md:py-28 px-4 md:px-6 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <span className="text-xs md:text-sm font-semibold text-emerald-600 uppercase tracking-widest">Planes</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-neutral-900 mt-3 mb-4">
              Modalidades de Contratación
            </h2>
            <p className="text-neutral-500 text-base md:text-lg max-w-2xl mx-auto">
              Elija la modalidad que mejor se adapte a las necesidades y presupuesto de su organización.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-neutral-100 hover:border-emerald-200 transition-all duration-300 flex flex-col">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full mb-4">Más Popular</span>
                <h3 className="font-outfit text-2xl md:text-3xl font-bold text-neutral-900">Licencia Permanente</h3>
                <p className="text-neutral-500 text-sm mt-2">Inversión única para operar sin pagos recurrentes</p>
              </div>
              <div className="flex-1 space-y-4 mb-8">
                {['Pago único, sin mensualidades', 'Soporte técnico incluido por 1 año', 'Actualizaciones gratuitas', 'Todos los módulos disponibles', 'Usuarios ilimitados'].map((feat, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs text-emerald-500"></i>
                    </div>
                    <span className="text-neutral-700 text-sm">{feat}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => scrollToSection('contacto')}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300"
              >
                Solicitar Información
              </button>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 border border-neutral-100 hover:border-orange-200 transition-all duration-300 flex flex-col">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-orange-50 text-orange-500 text-xs font-semibold rounded-full mb-4">Flexible</span>
                <h3 className="font-outfit text-2xl md:text-3xl font-bold text-neutral-900">Arriendo Anual</h3>
                <p className="text-neutral-500 text-sm mt-2">Modalidad flexible que incluye 2 meses sin costo</p>
              </div>
              <div className="flex-1 space-y-4 mb-8">
                {['Pago anual accesible', '2 meses gratis al contratar', 'Soporte técnico permanente', 'Todos los módulos disponibles', 'Usuarios ilimitados'].map((feat, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs text-orange-500"></i>
                    </div>
                    <span className="text-neutral-700 text-sm">{feat}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => scrollToSection('contacto')}
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300"
              >
                Solicitar Información
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="contacto" className="relative py-24 md:py-32 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src={CTA_IMAGE} alt="Abstract water flow pattern" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <h2 className="font-outfit text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Solicite una
                <span className="text-emerald-400"> Demostración</span>
              </h2>
              <p className="text-white/70 text-base md:text-lg mb-8 leading-relaxed max-w-lg">
                Descubra cómo APR-Online puede transformar la gestión de su Servicio Sanitario Rural. Agende una demo personalizada con nuestro equipo.
              </p>
              <div className="flex flex-col gap-3">
                <a href="https://wa.me/56953907106" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                    <i className="ri-whatsapp-line text-white"></i>
                  </div>
                  <span className="text-sm md:text-base">+56 9 5390 7106</span>
                </a>
                <a href="https://wa.me/56942885144" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                    <i className="ri-whatsapp-line text-white"></i>
                  </div>
                  <span className="text-sm md:text-base">+56 9 4288 5144</span>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8">
              <h3 className="font-outfit text-xl md:text-2xl font-bold text-neutral-900 mb-6">Contáctenos</h3>
              {formStatus === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-emerald-500"></i>
                  </div>
                  <h4 className="font-outfit text-lg font-bold text-neutral-900 mb-2">¡Solicitud enviada!</h4>
                  <p className="text-neutral-500 text-sm">Nos pondremos en contacto con usted a la brevedad.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Nombre completo</label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 transition-all"
                      placeholder="Ingrese su nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Correo electrónico</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 transition-all"
                      placeholder="correo@ejemplo.cl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Teléfono</label>
                    <input
                      type="tel"
                      name="telefono"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 transition-all"
                      placeholder="+56 9 XXXX XXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Nombre del APR</label>
                    <input
                      type="text"
                      name="nombre_apr"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 transition-all"
                      placeholder="Nombre de su APR o SSR"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Mensaje</label>
                    <textarea
                      name="mensaje"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 transition-all resize-none"
                      placeholder="Cuéntenos sobre su APR y qué necesita..."
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    {formStatus === 'submitting' ? (
                      <>Enviando... <i className="ri-loader-4-line animate-spin"></i></>
                    ) : (
                      <>Enviar Solicitud <i className="ri-send-plane-line"></i></>
                    )}
                  </button>
                  {formStatus === 'error' && (
                    <p className="text-red-500 text-sm text-center mt-2">Hubo un error. Intente nuevamente o llámenos directamente.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f0f0f] text-white py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mb-12">
            <div className="md:col-span-2">
              <a href="#" className="flex items-center gap-2.5 group">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl overflow-hidden bg-emerald-50/10 flex items-center justify-center">
                  <img
                    src={LOGO_IMAGE}
                    alt="APR-Online Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="font-outfit font-bold text-xl md:text-2xl tracking-tight group-hover:text-white">
                  APR<span className="text-emerald-400">-</span>Online
                </span>
              </a>
              <p className="text-neutral-400 text-sm mt-4 mb-6 max-w-sm leading-relaxed">
                La plataforma que moderniza la gestión de los Servicios Sanitarios Rurales en Chile. Facturación, lecturas, recaudación y más, todo integrado.
              </p>
            </div>
            <div>
              <h4 className="font-outfit text-sm font-semibold text-white mb-4">Enlaces Rápidos</h4>
              <div className="flex flex-col gap-2.5">
                {['Inicio', 'Módulos', 'Beneficios', 'Testimonios', 'Planes', 'Contacto'].map((link) => (
                  <button
                    key={link}
                    onClick={() => scrollToSection(link === 'Inicio' ? 'hero' : link === 'Módulos' ? 'modulos' : link === 'Beneficios' ? 'beneficios' : link === 'Testimonios' ? 'testimonios' : link === 'Planes' ? 'planes' : 'contacto')}
                    className="text-neutral-400 hover:text-white text-sm cursor-pointer transition-colors text-left"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-outfit text-sm font-semibold text-white mb-4">Contacto</h4>
              <div className="flex flex-col gap-2.5">
                <a href="https://wa.me/56953907106" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-green-400 text-sm transition-colors flex items-center gap-2">
                  <i className="ri-whatsapp-line"></i> +56 9 5390 7106
                </a>
                <a href="https://wa.me/56942885144" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-green-400 text-sm transition-colors flex items-center gap-2">
                  <i className="ri-whatsapp-line"></i> +56 9 4288 5144
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-neutral-500 text-xs">© 2026 APR-Online. Todos los derechos reservados.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-neutral-500 hover:text-white text-xs transition-colors">Términos y Condiciones</a>
              <a href="#" className="text-neutral-500 hover:text-white text-xs transition-colors">Política de Privacidad</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/56953907106"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center shadow-lg border-2 border-white transition-all duration-300"
      >
        <i className="ri-whatsapp-line text-white text-xl"></i>
      </a>

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "APR-Online | Gestión Moderna para Servicios Sanitarios Rurales",
          "description": "Plataforma integral para la gestión de Servicios Sanitarios Rurales: facturación electrónica, lectura móvil, recaudación digital y portal de usuarios.",
          "provider": {
            "@type": "Organization",
            "name": "APR-Online",
            "logo": LOGO_IMAGE,
            "telephone": "+56953907106"
          }
        })
      }} />
    </div>
  );
}